import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { formatCurrencyAmount } from '../../../theme/tokens';
import { useNavigate, useParams } from 'react-router-dom';
import { ArrowLeftIcon, DownloadIcon, MailIcon, EditIcon, PrinterIcon, CheckCircleIcon, XCircleIcon, MoreHorizontalIcon, ArchiveIcon, TrashIcon, UserIcon, PhoneIcon, MapPinIcon, CreditCardIcon, TagIcon, FileTextIcon, AlertTriangleIcon, ClockIcon, PackageIcon, TruckIcon, CalendarIcon, CheckIcon } from 'lucide-react';
import { Order, OrderStatus } from './Orders';
export function OrderDetail() {
  const {
    t
  } = useTranslation('shops');
  const navigate = useNavigate();
  const {
    id
  } = useParams();
  const [showMoreMenu, setShowMoreMenu] = useState(false);
  // Mock data based on the ID or default
  const order: Order = {
    id: id || '220629-1e753',
    status: 'new',
    paymentStatus: 'paid',
    customer: {
      name: 'randy',
      email: 'randy@randy.com',
      address: 'randy',
      mobileNumber: '6596605522',
      additionalPhone: '1234',
      address2: '1234'
    },
    items: [{
      name: 'Kittens',
      quantity: 2,
      price: 111.0
    }],
    total: 222.0,
    date: '29/06/2022',
    time: '14:28',
    transaction: '62bc1b4be3c2250009f1e753',
    transactionStatus: 'confirmed',
    notes: 'Please deliver between 9 AM and 5 PM.',
    shopId: '1',
    shopName: 'Beach Shop'
  };
  const getStatusBadge = (status: OrderStatus) => {
    const styles = {
      new: 'bg-blue-50 text-blue-700 border-blue-200',
      'in-progress': 'bg-amber-50 text-amber-700 border-amber-200',
      open: 'bg-purple-50 text-purple-700 border-purple-200',
      completed: 'bg-green-50 text-green-700 border-green-200',
      cancelled: 'bg-red-50 text-red-700 border-red-200'
    };
    const labels = {
      new: 'New',
      'in-progress': 'In Progress',
      open: 'Open',
      completed: 'Completed',
      cancelled: 'Cancelled'
    };
    return <span className={`inline-flex items-center px-2.5 py-0.5 rounded-md text-xs font-medium border ${styles[status]}`} data-id="element-11641">
        {labels[status]}
      </span>;
  };
  const getPaymentMethod = (transaction?: string) => {
    if (!transaction) return 'Unknown payment method';
    if (transaction.startsWith('62')) return 'Credit Card';
    return 'Bank Transfer';
  };
  const getShippingStatus = (status: OrderStatus) => {
    switch (status) {
      case 'new':
        return 'Awaiting processing';
      case 'in-progress':
        return 'Processing';
      case 'open':
        return 'Ready for pickup';
      case 'completed':
        return 'Delivered';
      case 'cancelled':
        return 'Cancelled';
      default:
        return 'Unknown';
    }
  };
  const formatDate = (dateString: string) => {
    const parts = dateString.split('/');
    if (parts.length !== 3) return dateString;
    const months = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];
    const day = parseInt(parts[0]);
    const month = parseInt(parts[1]) - 1;
    const year = parseInt(parts[2]);
    return `${day} ${months[month]} ${year}`;
  };
  return <div className="max-w-[1400px] mx-auto space-y-6" data-id="element-11642">
      {/* Header */}
      <div className="flex items-center justify-between" data-id="element-11643">
        <div className="flex items-center gap-4" data-id="element-11644">
          <button onClick={() => navigate('/orders')} className="p-2 border border-gray-200 rounded-lg text-gray-600 hover:bg-gray-50 transition-colors" data-id="element-11645">
            <ArrowLeftIcon size={18} data-id="element-11646" />
          </button>
          <div data-id="element-11647">
            <div className="flex items-center gap-3" data-id="element-11648">
              <h1 className="text-xl font-semibold text-gray-900" data-id="element-11649">
                {t('pages.OrderDetail.order')}
                {order.id}
              </h1>
              {getStatusBadge(order.status)}
            </div>
            <p className="text-sm text-gray-500 mt-0.5" data-id="element-11650">
              {order.customer.name}
            </p>
          </div>
        </div>

        <div className="flex items-center gap-2" data-id="element-11651">
          <button className="flex items-center gap-2 px-4 py-2 border border-gray-200 rounded-lg text-gray-700 hover:bg-gray-50 text-sm font-medium transition-colors" data-id="element-11652">
            <MailIcon size={16} data-id="element-11653" />
            {t('pages.OrderDetail.send')}
          </button>
          <button className="flex items-center gap-2 px-4 py-2 border border-gray-200 rounded-lg text-gray-700 hover:bg-gray-50 text-sm font-medium transition-colors" data-id="element-11654">
            <DownloadIcon size={16} data-id="element-11655" />
            {t('pages.OrderDetail.download')}
          </button>
          <button className="flex items-center gap-2 px-4 py-2 bg-[#E2231A] hover:bg-[#C41B14] text-white text-sm font-medium rounded-lg transition-colors shadow-sm" data-id="element-11656">
            <CheckCircleIcon size={16} data-id="element-11657" />
            {t('pages.OrderDetail.mark_fulfilled')}
          </button>
          <div className="relative" data-id="element-11658">
            <button onClick={() => setShowMoreMenu(!showMoreMenu)} aria-label="More actions" aria-expanded={showMoreMenu} className="p-2 border border-gray-200 rounded-lg text-gray-600 hover:bg-gray-50 transition-colors" data-id="element-11659">
              <MoreHorizontalIcon size={18} aria-hidden="true" data-id="element-11660" />
            </button>

            {showMoreMenu && <>
                <div className="fixed inset-0 z-10" onClick={() => setShowMoreMenu(false)} data-id="element-11661" />

                <div className="absolute right-0 mt-2 w-48 bg-white rounded-lg shadow-lg border border-gray-200 py-1 z-20" data-id="element-11662">
                  <button onClick={() => setShowMoreMenu(false)} className="w-full flex items-center px-4 py-2 text-sm text-gray-700 hover:bg-gray-50" data-id="element-11663">
                    <PrinterIcon size={16} className="mr-3 text-gray-400" data-id="element-11664" />
                    {t('pages.OrderDetail.print_order')}
                  </button>
                  <button onClick={() => setShowMoreMenu(false)} className="w-full flex items-center px-4 py-2 text-sm text-gray-700 hover:bg-gray-50" data-id="element-11665">
                    <EditIcon size={16} className="mr-3 text-gray-400" data-id="element-11666" />
                    {t('pages.OrderDetail.edit_order')}
                  </button>
                  <div className="border-t border-gray-100 my-1" data-id="element-11667" />
                  <button onClick={() => setShowMoreMenu(false)} className="w-full flex items-center px-4 py-2 text-sm text-red-600 hover:bg-red-50" data-id="element-11668">
                    <TrashIcon size={16} className="mr-3" data-id="element-11669" />
                    {t('pages.OrderDetail.cancel_order')}
                  </button>
                </div>
              </>}
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6" data-id="element-11670">
        {/* Main Content */}
        <div className="lg:col-span-2 space-y-6" data-id="element-11671">
          {/* Order Items */}
          <div className="bg-white rounded-xl border border-gray-200 overflow-hidden shadow-sm" data-id="element-11672">
            <div className="px-6 py-4 border-b border-gray-100 flex justify-between items-center" data-id="element-11673">
              <h2 className="text-base font-semibold text-gray-900 flex items-center gap-2" data-id="element-11674">
                <PackageIcon size={18} className="text-gray-500" data-id="element-11675" />
                {t('pages.OrderDetail.order_items')}
              </h2>
            </div>
            <div className="p-6" data-id="element-11676">
              <div className="space-y-4" data-id="element-11677">
                {order.items.map((item, index) => <div key={index} className="flex justify-between border-b border-gray-100 pb-4 last:border-0 last:pb-0" data-id="element-11678">
                    <div data-id="element-11679">
                      <div className="font-medium text-gray-900" data-id="element-11680">
                        {item.name}
                      </div>
                      <div className="text-sm text-gray-500 mt-1" data-id="element-11681">
                        {t('pages.OrderDetail.quantity')} {item.quantity} ×{' '}
                        {formatCurrencyAmount(item.price, {
                      fractionDigits: 2
                    })}
                      </div>
                    </div>
                    <div className="font-medium text-gray-900" data-id="element-11682">
                      {formatCurrencyAmount(item.price * item.quantity, {
                    fractionDigits: 2
                  })}
                    </div>
                  </div>)}
              </div>

              <div className="mt-6 border-t border-gray-100 pt-4 space-y-3" data-id="element-11683">
                <div className="flex justify-between text-sm" data-id="element-11684">
                  <span className="text-gray-500" data-id="element-11685">
                    {t('pages.OrderDetail.subtotal')}
                  </span>
                  <span className="text-gray-900 font-medium" data-id="element-11686">
                    {formatCurrencyAmount(order.total, {
                    fractionDigits: 2
                  })}
                  </span>
                </div>
                <div className="flex justify-between text-sm" data-id="element-11687">
                  <span className="text-gray-500" data-id="element-11688">
                    {t('pages.OrderDetail.shipping')}
                  </span>
                  <span className="text-gray-900 font-medium" data-id="element-11689">
                    {formatCurrencyAmount(0, {
                    fractionDigits: 2
                  })}
                  </span>
                </div>
                <div className="flex justify-between text-sm" data-id="element-11690">
                  <span className="text-gray-500" data-id="element-11691">
                    {t('pages.OrderDetail.tax')}
                  </span>
                  <span className="text-gray-900 font-medium" data-id="element-11692">
                    {formatCurrencyAmount(0, {
                    fractionDigits: 2
                  })}
                  </span>
                </div>
                <div className="flex justify-between text-base font-semibold pt-3 border-t border-gray-100" data-id="element-11693">
                  <span className="text-gray-900" data-id="element-11694">
                    {t('pages.OrderDetail.total')}
                  </span>
                  <span className="text-[#9F140F]" data-id="element-11695">
                    {formatCurrencyAmount(order.total, {
                    fractionDigits: 2
                  })}
                  </span>
                </div>
              </div>
            </div>
          </div>

          {/* Order Timeline */}
          <div className="bg-white rounded-xl border border-gray-200 overflow-hidden shadow-sm" data-id="element-11696">
            <div className="px-6 py-4 border-b border-gray-100" data-id="element-11697">
              <h2 className="text-base font-semibold text-gray-900 flex items-center gap-2" data-id="element-11698">
                <ClockIcon size={18} className="text-gray-500" data-id="element-11699" />
                {t('pages.OrderDetail.order_timeline')}
              </h2>
            </div>
            <div className="p-6" data-id="element-11700">
              <div className="space-y-6" data-id="element-11701">
                <div className="flex items-start" data-id="element-11702">
                  <div className="flex-shrink-0 h-6 w-6 rounded-full bg-green-100 flex items-center justify-center mt-0.5" data-id="element-11703">
                    <CheckIcon size={14} className="text-green-600" data-id="element-11704" />
                  </div>
                  <div className="ml-4" data-id="element-11705">
                    <p className="text-sm font-medium text-gray-900" data-id="element-11706">
                      {t('pages.OrderDetail.order_placed')}
                    </p>
                    <p className="text-xs text-gray-500 mt-1" data-id="element-11707">
                      {formatDate(order.date)} {t('pages.OrderDetail.at')}{' '}
                      {order.time}
                    </p>
                  </div>
                </div>

                {order.status === 'in-progress' || order.status === 'completed' ? <div className="flex items-start" data-id="element-11708">
                    <div className="flex-shrink-0 h-6 w-6 rounded-full bg-amber-100 flex items-center justify-center mt-0.5" data-id="element-11709">
                      <CheckIcon size={14} className="text-amber-600" data-id="element-11710" />
                    </div>
                    <div className="ml-4" data-id="element-11711">
                      <p className="text-sm font-medium text-gray-900" data-id="element-11712">
                        {t('pages.OrderDetail.processing_started')}
                      </p>
                      <p className="text-xs text-gray-500 mt-1" data-id="element-11713">
                        {t('pages.OrderDetail.order_is_being_processed')}
                      </p>
                    </div>
                  </div> : <div className="flex items-start" data-id="element-11714">
                    <div className="flex-shrink-0 h-6 w-6 rounded-full bg-gray-100 flex items-center justify-center mt-0.5" data-id="element-11715">
                      <span className="text-xs font-medium text-gray-500" data-id="element-11716">
                        2
                      </span>
                    </div>
                    <div className="ml-4" data-id="element-11717">
                      <p className="text-sm font-medium text-gray-500" data-id="element-11718">
                        {t('pages.OrderDetail.processing')}
                      </p>
                      <p className="text-xs text-gray-400 mt-1" data-id="element-11719">
                        {t('pages.OrderDetail.waiting_to_be_processed')}
                      </p>
                    </div>
                  </div>}

                {order.status === 'completed' ? <div className="flex items-start" data-id="element-11720">
                    <div className="flex-shrink-0 h-6 w-6 rounded-full bg-green-100 flex items-center justify-center mt-0.5" data-id="element-11721">
                      <CheckIcon size={14} className="text-green-600" data-id="element-11722" />
                    </div>
                    <div className="ml-4" data-id="element-11723">
                      <p className="text-sm font-medium text-gray-900" data-id="element-11724">
                        {t('components.getOrderStatusBadge.completed')}
                      </p>
                      <p className="text-xs text-gray-500 mt-1" data-id="element-11725">
                        {t('pages.OrderDetail.order_has_been_fulfilled')}
                      </p>
                    </div>
                  </div> : <div className="flex items-start" data-id="element-11726">
                    <div className="flex-shrink-0 h-6 w-6 rounded-full bg-gray-100 flex items-center justify-center mt-0.5" data-id="element-11727">
                      <span className="text-xs font-medium text-gray-500" data-id="element-11728">
                        3
                      </span>
                    </div>
                    <div className="ml-4" data-id="element-11729">
                      <p className="text-sm font-medium text-gray-500" data-id="element-11730">
                        {t('components.getOrderStatusBadge.completed')}
                      </p>
                      <p className="text-xs text-gray-400 mt-1" data-id="element-11731">
                        {t('pages.OrderDetail.waiting_to_be_completed')}
                      </p>
                    </div>
                  </div>}
              </div>
            </div>
          </div>
        </div>

        {/* Sidebar */}
        <div className="space-y-6" data-id="element-11732">
          {/* Customer Information */}
          <div className="bg-white rounded-xl border border-gray-200 overflow-hidden shadow-sm" data-id="element-11733">
            <div className="px-6 py-4 border-b border-gray-100" data-id="element-11734">
              <h2 className="text-base font-semibold text-gray-900 flex items-center gap-2" data-id="element-11735">
                <UserIcon size={18} className="text-gray-500" data-id="element-11736" />
                {t('pages.OrderDetail.customer_information')}
              </h2>
            </div>
            <div className="p-6 space-y-4" data-id="element-11737">
              <div className="flex items-center gap-3 mb-2" data-id="element-11738">
                <div className="w-10 h-10 bg-[#E2231A]/10 rounded-full flex items-center justify-center text-[#9F140F] font-semibold" data-id="element-11739">
                  {order.customer.name.charAt(0).toUpperCase()}
                </div>
                <div data-id="element-11740">
                  <div className="font-medium text-gray-900" data-id="element-11741">
                    {order.customer.name}
                  </div>
                  <div className="text-xs text-gray-500" data-id="element-11742">
                    {t('pages.OrderDetail.customer')}
                    {order.id.split('-')[0]}
                  </div>
                </div>
              </div>

              <div className="space-y-3 pt-2 border-t border-gray-100" data-id="element-11743">
                <div className="flex items-start gap-3" data-id="element-11744">
                  <MailIcon size={16} className="text-gray-400 mt-0.5" data-id="element-11745" />
                  <div data-id="element-11746">
                    <div className="text-sm text-gray-900" data-id="element-11747">
                      {order.customer.email}
                    </div>
                    <div className="text-xs text-gray-500" data-id="element-11748">
                      {t('pages.OrderDetail.email')}
                    </div>
                  </div>
                </div>
                <div className="flex items-start gap-3" data-id="element-11749">
                  <PhoneIcon size={16} className="text-gray-400 mt-0.5" data-id="element-11750" />
                  <div data-id="element-11751">
                    <div className="text-sm text-gray-900" data-id="element-11752">
                      {order.customer.mobileNumber}
                    </div>
                    <div className="text-xs text-gray-500" data-id="element-11753">
                      {t('pages.OrderDetail.mobile')}
                    </div>
                  </div>
                </div>
                {order.customer.additionalPhone && <div className="flex items-start gap-3" data-id="element-11754">
                    <PhoneIcon size={16} className="text-gray-400 mt-0.5" data-id="element-11755" />
                    <div data-id="element-11756">
                      <div className="text-sm text-gray-900" data-id="element-11757">
                        {order.customer.additionalPhone}
                      </div>
                      <div className="text-xs text-gray-500" data-id="element-11758">
                        {t('pages.OrderDetail.additional_phone')}
                      </div>
                    </div>
                  </div>}
                <div className="flex items-start gap-3" data-id="element-11759">
                  <MapPinIcon size={16} className="text-gray-400 mt-0.5" data-id="element-11760" />
                  <div data-id="element-11761">
                    <div className="text-sm text-gray-900" data-id="element-11762">
                      {order.customer.address}
                    </div>
                    {order.customer.address2 && <div className="text-sm text-gray-900 mt-0.5" data-id="element-11763">
                        {order.customer.address2}
                      </div>}
                    <div className="text-xs text-gray-500 mt-0.5" data-id="element-11764">
                      {t('pages.OrderDetail.shipping_address')}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Payment Information */}
          <div className="bg-white rounded-xl border border-gray-200 overflow-hidden shadow-sm" data-id="element-11765">
            <div className="px-6 py-4 border-b border-gray-100" data-id="element-11766">
              <h2 className="text-base font-semibold text-gray-900 flex items-center gap-2" data-id="element-11767">
                <CreditCardIcon size={18} className="text-gray-500" data-id="element-11768" />
                {t('pages.OrderDetail.payment_information')}
              </h2>
            </div>
            <div className="p-6 space-y-4" data-id="element-11769">
              <div className="flex items-start gap-3" data-id="element-11770">
                <CreditCardIcon size={16} className="text-gray-400 mt-0.5" data-id="element-11771" />
                <div data-id="element-11772">
                  <div className="text-sm text-gray-900" data-id="element-11773">
                    {getPaymentMethod(order.transaction)}
                  </div>
                  <div className="text-xs text-gray-500" data-id="element-11774">
                    {t('pages.OrderDetail.payment_method')}
                  </div>
                </div>
              </div>
              <div className="flex items-start gap-3" data-id="element-11775">
                <TagIcon size={16} className="text-gray-400 mt-0.5" data-id="element-11776" />
                <div data-id="element-11777">
                  <div className="text-sm text-gray-900 capitalize" data-id="element-11778">
                    {order.paymentStatus}
                  </div>
                  <div className="text-xs text-gray-500" data-id="element-11779">
                    {t('pages.OrderDetail.payment_status')}
                  </div>
                </div>
              </div>
              {order.transaction && <div className="flex items-start gap-3" data-id="element-11780">
                  <FileTextIcon size={16} className="text-gray-400 mt-0.5" data-id="element-11781" />
                  <div data-id="element-11782">
                    <div className="text-sm text-gray-900 break-all" data-id="element-11783">
                      {order.transaction}
                    </div>
                    <div className="text-xs text-gray-500" data-id="element-11784">
                      {t('pages.OrderDetail.transaction_id')}
                    </div>
                  </div>
                </div>}
              {order.transactionStatus && <div className="flex items-start gap-3" data-id="element-11785">
                  {order.transactionStatus === 'confirmed' ? <CheckCircleIcon size={16} className="text-green-500 mt-0.5" data-id="element-11786" /> : order.transactionStatus === 'failed' ? <AlertTriangleIcon size={16} className="text-red-500 mt-0.5" data-id="element-11787" /> : <ClockIcon size={16} className="text-amber-500 mt-0.5" data-id="element-11788" />}
                  <div data-id="element-11789">
                    <div className="text-sm text-gray-900 capitalize" data-id="element-11790">
                      {order.transactionStatus}
                    </div>
                    <div className="text-xs text-gray-500" data-id="element-11791">
                      {t('pages.OrderDetail.transaction_status')}
                    </div>
                  </div>
                </div>}
            </div>
          </div>

          {/* Shipping Status */}
          <div className="bg-white rounded-xl border border-gray-200 overflow-hidden shadow-sm" data-id="element-11792">
            <div className="px-6 py-4 border-b border-gray-100" data-id="element-11793">
              <h2 className="text-base font-semibold text-gray-900 flex items-center gap-2" data-id="element-11794">
                <TruckIcon size={18} className="text-gray-500" data-id="element-11795" />
                {t('pages.OrderDetail.shipping_status')}
              </h2>
            </div>
            <div className="p-6" data-id="element-11796">
              <div className="text-sm font-medium text-gray-900 mb-4" data-id="element-11797">
                {getShippingStatus(order.status)}
              </div>
              {order.notes && <div className="bg-gray-50 rounded-lg p-3 border border-gray-100" data-id="element-11798">
                  <div className="text-xs font-medium text-gray-700 mb-1" data-id="element-11799">
                    {t('pages.OrderDetail.customer_notes')}
                  </div>
                  <p className="text-sm text-gray-600" data-id="element-11800">{order.notes}</p>
                </div>}
            </div>
          </div>
        </div>
      </div>
    </div>;
}